import React, { useState, useMemo, useEffect, useContext } from 'react';
import { withRouter } from 'react-router-dom';

import AuthApi, { MfaChallengeType, UserAuthDTO, UserOrganizationalUnitRoleAuthDTO } from '../api/AuthApi';
import Loader from '../components/Loader';
import useLoader from '../util/useLoader';
import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';

export const AuthContext = React.createContext<AuthContextValue>(null as unknown as AuthContextValue);

export function useAuthContext() {
  return useContext(AuthContext);
}

type OrganizationalUnitRole = {
  id: number
  userId: number
  organizationalUnitId: number
  organizationalUnit: OrganizationalUnit
} & RoleWithFlags;
type Role = {
  name: string
}
type RoleWithFlags = {
  roleId: number
  role: Role
  mainRole: boolean
  canWorkAs: boolean
  canBeAssigned: boolean
  canAssign: boolean
}
type OrganizationalUnit = {
  id: number
};
type UserOrganizationalUnitEntry = OrganizationalUnit & {
  roles: RoleWithFlags[]
};

type AuthContextUser = UserAuthDTO & {
  actualOrganizationalUnitId?: number
}

export abstract class UserStoreUser {
  email: string
  logOutUrl: string
  user: AuthContextUser
  actualUser?: UserStoreUser | null
  permissions: Record<string, 1>
  organizationalUnitRoles: UserOrganizationalUnitRoleAuthDTO[]
  organizationalUnits: UserOrganizationalUnitEntry[]
  rolesByName: Record<number, Record<string, OrganizationalUnitRole>>
  get name(){
    return this?.user
  }

  constructor({
    user,
    logOutUrl,
    actualUser,
  }: {
    user: AuthContextUser,
    logOutUrl: string,
    actualUser?: UserStoreUser | null,
  }) {
    this.email = user.email;
    this.logOutUrl = logOutUrl;
    this.user = user;
    this.actualUser = actualUser;
    this.permissions = user.accessProfile.permissions.reduce((_, p) => {
      _[p] = 1;

      return _;
    }, {} as Record<string, 1>);
    this.organizationalUnitRoles = user.organizationalUnitRoles;
    this.organizationalUnits = user.organizationalUnitRoles.reduce(
      (_, { id, userId, organizationalUnit, ...roleWithFlags }) => {
        if (!_.index[organizationalUnit.id]) {
          const entry = { ...organizationalUnit, roles: [] };
          _.list.push(entry);
          _.index[organizationalUnit.id] = entry;
        }

        _.index[organizationalUnit.id].roles.push(roleWithFlags);

        return _;
      },
      { list: [], index: {} } as {
         list: UserOrganizationalUnitEntry[],
         index: Record<number, UserOrganizationalUnitEntry>
      }
    ).list;
    this.rolesByName = user.organizationalUnitRoles.reduce((acc, our) => {
      const orgId = our.organizationalUnitId;
      const roleName = our.role.name;

      if (!acc[orgId]) {
        acc[orgId] = {};
      }

      acc[orgId][roleName] = our;

      return acc;
    }, {} as Record<number, Record<string, OrganizationalUnitRole>>);
  }

  abstract stringify(): string;
}

class InternalUserStoreUser extends UserStoreUser {
  token: string
  constructor({
    user,
    token,
    logOutUrl,
    actualUser,
  }: {
    user: AuthContextUser,
    token: string,
    logOutUrl: string,
    actualUser?: UserStoreUser | null,
  }) {
    super({ user, logOutUrl, actualUser });
    this.token = token;
  }

  static parse(json?: string | null): InternalUserStoreUser | null {
    const obj = json ? JSON.parse(json) : null;
    if (!obj) return null
    const { user, token, logOutUrl, actualUser } = obj;
    const actualUserInstance: InternalUserStoreUser | null =
      actualUser
        ? InternalUserStoreUser.parse(JSON.stringify(actualUser))
        : null;

    return new InternalUserStoreUser({
      user,
      token,
      logOutUrl,
      actualUser: actualUserInstance,
    });
  }

  stringify() {
    const { user, token, logOutUrl, actualUser } = this;
    return JSON.stringify({ user, token, logOutUrl, actualUser })
  }
}

export type MFAInfo = {
  token: string
  type?: MfaChallengeType
}

export class UserStore {
  KEY = 'user';
  MFA_KEY = 'mfa';
  user: UserStoreUser | null | undefined = null
  mfa: MFAInfo | null = null

  constructor() {}

  static init() {
    const store = new UserStore();
    const json = localStorage.getItem(store.KEY);

    try {
      store.user = InternalUserStoreUser.parse(json);
    } catch {
      // Stored user doesn't match the current shape (stale session from before a DTO change,
      // corrupted JSON, etc.) — drop it and fall through to a logged-out state instead of
      // crashing the whole app before it can even render a login screen.
      localStorage.removeItem(store.KEY);
      store.user = null;
    }

    return store;
  }

  buildUserFrom(
    user: AuthContextUser,
    token: string,
    logOutUrl: string,
    actualUser?: UserStoreUser | null
  ) {
    if (user && user.actualOrganizationalUnitId === undefined) {
      user.actualOrganizationalUnitId = user?.organizationalUnit?.id;
    }

    return new InternalUserStoreUser({
      user,
      token,
      logOutUrl,
      actualUser,
    });
  }

  setUser(newUser?: UserStoreUser) {
    this.user = newUser;

    if (newUser) {
      localStorage.setItem(this.KEY, newUser.stringify());
    } else {
      localStorage.removeItem(this.KEY);
    }

    // Cancel out any mfa token
    this.mfa = null;
  }

  setMfaInfo(token: string, type?: MfaChallengeType) {
    this.mfa = { token, type };
  }

  deleteMfaInfo() {
    this.mfa = null;
  }

  getMfaToken() {
    return this.mfa?.token;
  }
}

const USER_STORE = UserStore.init();

function _AuthProvider(props: {
  children: React.ReactNode
}) {
  const { children } = props;
  const [loading, , loadFn] = useLoader(true);
  const [updateCt, setUpdateCt] = useState(0);

  const value = useMemo(() => new AuthContextValue(USER_STORE, () => setUpdateCt(Math.random() + new Date().getTime())), [USER_STORE, updateCt]);

  useEffect(() => {
    loadFn(() => value.verifyToken());
  }, []);

  return <AuthContext.Provider value={value}>{loading ? <Loader spinner fullscreen /> : children}</AuthContext.Provider>;
}

export function getToken() {
  return ((USER_STORE.user || {}) as InternalUserStoreUser).token;
}

export type SignalUpdateFn = () => void;
export type OTP = any;
type MSALInstance = IPublicClientApplication;

export class AuthContextValue {
  userStore: UserStore;
  signalUpdate: SignalUpdateFn;
  user: UserStoreUser | null | undefined
  isLoggedIn: boolean
  isPendingMfaVerification: boolean
  mfaType: MfaChallengeType | undefined

  constructor(userStore: UserStore, signalUpdate: SignalUpdateFn) {
    this.userStore = userStore;
    this.signalUpdate = signalUpdate;
    this.user = userStore.user;
    this.validateUser();
    this.isLoggedIn = !!userStore.user;
    this.isPendingMfaVerification = !!userStore.mfa;
    this.mfaType = userStore.mfa?.type;
  }

  async login({
    email,
    password,
    tenantSlug
  }: {
    email: string,
    password: string,
    /** Tenant entry point the sign-in came through; see AuthApi.login. */
    tenantSlug?: string | null
  }) {
    const response = await AuthApi.login({ email, password, tenantSlug });
    const { token, logOutUrl, user, mfaVerificationRequired, mfaVerificationType } = response;

    if (!mfaVerificationRequired) {
      this.userStore.setUser(this.userStore.buildUserFrom(user, token, logOutUrl));
    } else {
      // Log that further MFA verification is needed
      this.userStore.setMfaInfo(token, mfaVerificationType);
    }

    this.signalUpdate();
  }

  validateUser() {
    if (this.user) {
      const user = this.user.user;
      if (user && !user.actualOrganizationalUnitId) {
        user.actualOrganizationalUnitId = user?.organizationalUnit?.id;
      }
    }
  }

  async verifyMfa({ otp }: { otp: OTP }) {
    const mfaToken = this.userStore.getMfaToken();

    const response = await AuthApi.verifyMfa({ otp, token: mfaToken });
    const { token, logOutUrl, user } = response;
    this.userStore.setUser(this.userStore.buildUserFrom(user, token, logOutUrl));

    this.signalUpdate();
  }

  // Do we define a route to tell the backend to CANCEL the challenge? Or not?
  cancelMfaVerification() {
    this.userStore.deleteMfaInfo();
    this.signalUpdate();
  }

  async loginWithToken({ token }: { token: string }) {
    const response = await AuthApi.loginWithToken({ token });
    const { token: authToken, logOutUrl, user } = response;
    this.userStore.setUser(this.userStore.buildUserFrom(user, authToken, logOutUrl));
    this.signalUpdate();
  }

  async loginWithSSO({
    ssoAccount,
    msalInstance
  }: {
    ssoAccount: AccountInfo,
    msalInstance: MSALInstance
  }) {
    if (!ssoAccount) return;

    const clientId = await AuthApi.getADClientId(undefined);
    if (!clientId) return;

    const tokenResponse = await msalInstance.acquireTokenSilent({
      scopes: [`api://${clientId}/Usage`],
      account: ssoAccount,
    });

    const accessToken = tokenResponse?.accessToken;
    if (!accessToken) return;

    // console.log("loginWithSSO", {ssoAccount, accessToken});

    return this.login({
      email: `sso:${ssoAccount.username}`,
      password: `token:${accessToken}`,
    });
  }

  async logout({
    msalInstance
  }: {
    msalInstance: MSALInstance
  }) {
    const logOutUrl = this.userStore?.user?.logOutUrl;
    const accounts = msalInstance?.getAllAccounts();
    this.userStore.setUser();

    // Handle SSO logout
    if (accounts?.length > 0) {
      await msalInstance.logoutRedirect({
        account: accounts[0],
      });
    }
    // Fallback to backend logout URL
    if (logOutUrl) {
      document.location = logOutUrl;
    } else {
      this.signalUpdate();
    }
  }

  setOrganizationalUnit(orgUnitId: number) {
    if (this.userStore?.user?.user) {
      this.userStore.user.user.actualOrganizationalUnitId = orgUnitId;
      this.userStore.setUser(this.userStore.user);
      this.signalUpdate();
    }
  }

  setCompletedProfile(hasCompletedProfile: boolean) {
    if (this.userStore?.user?.user) {
      this.userStore.user.user.hasCompletedProfile = hasCompletedProfile;
      this.userStore.setUser(this.userStore.user);
    }
  }

  setChangePassword(changePassword: boolean) {
    if (this.userStore?.user?.user) {
      this.userStore.user.user.changePassword = changePassword;
      this.userStore.setUser(this.userStore.user);
    }
  }

  async impersonate({ userId }: { userId: number }) {
    const actualUser = this.userStore.user;
    const response = await AuthApi.impersonate({ userId });
    const { token, logOutUrl, user } = response;
    this.userStore.setUser(this.userStore.buildUserFrom(user, token, logOutUrl, actualUser));
    this.signalUpdate();
  }

  async stopImpersonation() {
    const { actualUser } = this.userStore?.user ?? {};
    if (actualUser) {
      this.userStore.setUser(actualUser);
      this.signalUpdate();
    }
  }

  async verifyToken() {
    // console.log("verifying user token...")
    try {
      const verified = await AuthApi.verifyToken(undefined);

      if (!verified) {
        // console.log("token is invalid or stale, user is not logged in.");
        this.userStore.setUser();
        this.signalUpdate();
      } else {
        // console.log("user is still logged in.");
      }
    } catch (e) {
      // console.log("error while verifiyng token, user is not logged in.");
      this.userStore.setUser();
      this.signalUpdate();
    }
  }
}

export const AuthConsumer = AuthContext.Consumer;

export const AuthProvider = withRouter(_AuthProvider as any);
